import is from '@sindresorhus/is';
import { useEffect, useMemo, useState } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import type { TranslationKey } from '~/shared/internationalization/translation';

export const useSetupLanguageFromLocales = (
    locales: readonly string[] | undefined,
    preferredLanguage?: LanguageCodeForJSA | undefined,
): {
    readonly activeLanguage: LanguageCodeForJSA;
    readonly availableLanguages: readonly { readonly id: LanguageCodeForJSA; readonly name: string }[];
    readonly setActiveLanguage: (language: LanguageCodeForJSA) => void;
} => {
    const { applicationState, translate } = useApplicationContext();
    const currentLanguage = applicationState.language;

    const availableLanguages = useMemo(
        () =>
            (locales ?? []).map((locale) => ({
                id: locale,
                name: translate(`common.languages.${locale}` as TranslationKey),
            })),
        [locales, translate],
    );

    const initialLanguage = preferredLanguage ?? currentLanguage.code;
    const [activeLanguage, setActiveLanguage] = useState<LanguageCodeForJSA>(initialLanguage);

    useEffect(() => {
        if (is.string(preferredLanguage)) {
            setActiveLanguage(preferredLanguage);
        }
    }, [preferredLanguage]);

    useEffect(() => {
        if (availableLanguages.length === 0 || availableLanguages.some((language) => language.id === activeLanguage)) {
            return;
        }

        setActiveLanguage(availableLanguages[0]?.id ?? 'en-US');
    }, [availableLanguages, activeLanguage]);

    return {
        activeLanguage,
        availableLanguages,
        setActiveLanguage,
    };
};
